// Sentry Configuration for Error Tracking
// This file configures Sentry for production error monitoring

// Import Sentry packages
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

// Get Sentry DSN from environment (fallback to hardcoded for production)
const SENTRY_DSN = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SENTRY_DSN) ||
                   'https://6ccb819633350045da62d4e47af7309e@o4510238167007232.ingest.de.sentry.io/4510238180704336';

const isJest = typeof process !== 'undefined' && Boolean(process.env.JEST_WORKER_ID);
const isNodeTest = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';

function resolveSentryEnvironment() {
  if (isJest || isNodeTest) return 'test';
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
      return 'development';
    }
    if (host.includes('supercalcpro.com.au') || host.includes('supercalcpro.com')) {
      return 'production';
    }
  }
  return (typeof process !== 'undefined' && process.env.NODE_ENV) || 'development';
}

const sentryEnvironment = resolveSentryEnvironment();
const shouldInitSentry =
  !isJest &&
  !isNodeTest &&
  sentryEnvironment === 'production' &&
  SENTRY_DSN &&
  SENTRY_DSN.length > 0 &&
  SENTRY_DSN !== 'YOUR_SENTRY_DSN_HERE';

/** Safari WebKit can throw when native video controls probe buffered/played TimeRanges */
export function isSafariMediaEmptyRangesNoise(input) {
  const msg =
    typeof input === 'string'
      ? input
      : input?.message || input?.exception?.values?.[0]?.value || '';
  return /Can't find variable:\s*EmptyRanges|EmptyRanges is not defined/i.test(String(msg));
}

/** Stripe.js / embedded payment UIs often throw this when origin isn’t allowlisted — not an app bug */
function isBenignInvalidOriginError(event) {
  if (!event.exception?.values?.length) return false;
  const value = event.exception.values[0].value || '';
  const v = String(value).trim();
  return /^invalid origin\.?$/i.test(v) || /^error:\s*invalid origin\.?$/i.test(v);
}

/**
 * Browser extensions and in-app browsers dispatch a synthetic unhandledrejection.
 * Sentry then records either a CustomEvent, or an Error whose message is the string "undefined".
 */
function isSyntheticRejectionEvent(event) {
  const ex = event.exception?.values?.[0];
  const value = String(ex?.value ?? event.message ?? '').trim();
  const type = String(ex?.type || '');
  if (/captured as promise rejection/i.test(value)) return true;
  if (type === 'CustomEvent' || type === 'Event') return true;
  if (value === 'undefined' || value === 'null') return true;
  return false;
}

function isTestOrLocalNoise(event) {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
  for (const frame of frames) {
    const name = `${frame.filename || ''}${frame.abs_path || ''}`;
    if (
      /__tests__|\.test\.(js|jsx|ts|tsx)$|\.spec\.(js|jsx|ts|tsx)$|jest|@testing-library/i.test(name)
    ) {
      return true;
    }
  }
  const url = event.request?.url || '';
  if (/localhost|127\.0\.0\.1/i.test(url)) return true;
  return false;
}

// Initialize Sentry only on live production hosts (not Jest, not local dev)
if (shouldInitSentry && typeof window !== 'undefined') {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: sentryEnvironment,
    integrations: [
      new BrowserTracing(),
    ],
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    ignoreErrors: [
      /^Event `.+` \(type=unhandledrejection\) captured as promise rejection$/,
      /^undefined$/,
      /^null$/,
    ],
    beforeSend(event) {
      if (isSafariMediaEmptyRangesNoise(event)) {
        return null;
      }
      if (isBenignInvalidOriginError(event)) {
        return null;
      }
      if (isSyntheticRejectionEvent(event)) {
        return null;
      }
      if (isTestOrLocalNoise(event)) {
        return null;
      }
      if (event.exception) {
        const error = event.exception.values[0];
        if (error.type === 'ChunkLoadError' || error.type === 'Loading chunk') {
          return null; // Don't send chunk load errors
        }
      }
      return event;
    }
  });
  window.Sentry = Sentry; // Make Sentry available globally
}

// Export Sentry itself to prevent tree-shaking
export { Sentry };

// Export a function to manually capture errors
export const captureError = (error, context = {}) => {
  if (error == null) return;
  if (typeof Event !== 'undefined' && error instanceof Event) return;
  const message = typeof error === 'string' ? error : error?.message;
  if (message === 'undefined' || message === 'null' || /captured as promise rejection/i.test(String(message || ''))) {
    return;
  }
  if (isSafariMediaEmptyRangesNoise(error)) {
    return;
  }
  if (window.Sentry) {
    window.Sentry.captureException(error, {
      tags: {
        component: 'SuperCalc Pro',
        ...context
      }
    });
  }
};

// Export a function to capture messages
export const captureMessage = (message, level = 'info', context = {}) => {
  if (window.Sentry) {
    window.Sentry.captureMessage(message, {
      level,
      tags: {
        component: 'SuperCalc Pro',
        ...context
      }
    });
  }
};